import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Snackbar,
    Stack,
    Paper,
    Grid,
    Chip,
    Button,
    Tooltip,
    useTheme,
    alpha,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PendingIcon from '@mui/icons-material/Pending';
import {
    SessionQuestionEntityFrontend,
    fetchWorkerWithSessionQuestions,
    bulkUpdateSessionQuestionAnswers,
    BulkUpdateAnswerPayloadItem,
    WorkerDtoFrontend,
    getAnswerForQuestion,
} from '../../../api/sessionQuestionsApi';
import { Session } from '../../../models/session';
import { Worker } from '../../../models/worker';
import { getSessionByWorkerId } from '../../../api/chatApi';
import socketService from '../../../services/socketService';
import logger from '../../../utils/logger';
import { getDraft, saveDraft, clearDraft } from '../../../utils/localStorageDraft';
import {
    categoryOrder,
    sortQuestions,
    getAnswerAsString,
    getQuestionLabel,
    isAnswered as checkIsAnswered,
} from './questionFormConstants';
import QuestionFormSection from './QuestionFormSection';
import QuestionFormToolbar from './QuestionFormToolbar';
import QuestionHistoryModal from './QuestionHistoryModal';
import ListItemEditModal from '../ListItemEditModal';

export interface WorkerQuestionFormProps {
    workerId: string;
    worker?: Worker;
}

const DRAFT_KEY_PREFIX = 'questions:';

const WorkerQuestionForm: React.FC<WorkerQuestionFormProps> = ({ workerId, worker }) => {
    const theme = useTheme();

    // Server data — worker is the source of truth for answer values post-PR1.
    const [questions, setQuestions] = useState<SessionQuestionEntityFrontend[]>([]);
    const [workerDto, setWorkerDto] = useState<WorkerDtoFrontend | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Form draft state
    const [editedAnswers, setEditedAnswers] = useState<Record<number, string>>({});
    const [hasRemoteUpdate, setHasRemoteUpdate] = useState(false);
    const [restoredDraftCount, setRestoredDraftCount] = useState(0);

    // Modals
    const [historyQuestionId, setHistoryQuestionId] = useState<number | null>(null);
    const [listEditQuestionId, setListEditQuestionId] = useState<number | null>(null);

    // Snackbar
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false, message: '', severity: 'success',
    });

    // Ref to track dirty state for socket handler without stale closures
    const dirtyCountRef = useRef(0);

    const draftKey = DRAFT_KEY_PREFIX + workerId;

    // Computed: which question IDs are dirty
    const dirtyQuestionIds = useMemo(() => {
        const dirty = new Set<number>();
        for (const [idStr, draftValue] of Object.entries(editedAnswers)) {
            const id = Number(idStr);
            const question = questions.find(q => q.id === id);
            if (!question) continue;
            const original = getAnswerAsString(question, getAnswerForQuestion(question, workerDto));
            if (draftValue !== original) dirty.add(id);
        }
        return dirty;
    }, [editedAnswers, questions, workerDto]);

    const isDirty = dirtyQuestionIds.size > 0;
    dirtyCountRef.current = dirtyQuestionIds.size;

    // Stats
    const stats = useMemo(() => {
        const required = questions.filter(q => q.isRequiredAnswer);
        const requiredAnswered = required.filter(q => checkIsAnswered(q)).length;
        const totalAnswered = questions.filter(q => checkIsAnswered(q)).length;
        return {
            total: questions.length,
            totalAnswered,
            requiredTotal: required.length,
            requiredAnswered,
            requiredMissing: required.length - requiredAnswered,
        };
    }, [questions]);

    // Group questions by category
    const groupedQuestions = useMemo(() => {
        const groups: Record<string, SessionQuestionEntityFrontend[]> = {};
        for (const q of questions) {
            const cat = q.questionType;
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(q);
        }
        return groups;
    }, [questions]);

    // Share URL
    const shareUrl = useMemo(() => {
        if (!session?.publicShareId) return null;
        if (typeof window !== 'undefined' && window.location) {
            return `${window.location.origin}/session-share/${session.publicShareId}`;
        }
        return `/session-share/${session.publicShareId}`;
    }, [session?.publicShareId]);

    const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    // Fetch functions — pulls both worker + questions in one round trip.
    // Worker is the source of truth for answer values per PR1.
    const fetchQuestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const wrapped = await fetchWorkerWithSessionQuestions(workerId);
            setQuestions(sortQuestions(wrapped?.questions ?? []));
            setWorkerDto(wrapped?.worker ?? null);
        } catch (err: any) {
            setError(err.message || 'Failed to load questions.');
            setQuestions([]);
            setWorkerDto(null);
        } finally {
            setLoading(false);
        }
    }, [workerId]);

    const fetchSession = useCallback(async () => {
        try {
            const sessionData = await getSessionByWorkerId(workerId);
            setSession(sessionData);
        } catch (err) {
            logger.error('Failed to fetch session:', err);
        }
    }, [workerId]);

    // Initial load + draft restoration
    useEffect(() => {
        if (!workerId) return;

        const load = async () => {
            await Promise.all([fetchQuestions(), fetchSession()]);

            // Try to restore draft from localStorage
            const draft = getDraft<Record<number, string>>(draftKey);
            if (draft && Object.keys(draft).length > 0) {
                setEditedAnswers(draft);
                setRestoredDraftCount(Object.keys(draft).length);
            }
        };
        load();
    }, [workerId, fetchQuestions, fetchSession, draftKey]);

    // Socket.IO setup
    useEffect(() => {
        if (!workerId) return;

        const socket = socketService.connect();

        const handleQuestionUpdate = (data: { workerId: string }) => {
            if (data.workerId !== workerId) return;

            if (dirtyCountRef.current > 0) {
                setHasRemoteUpdate(true);
            } else {
                fetchQuestions();
                fetchSession();
            }
        };

        const setupListeners = () => {
            socket.emit('joinSessionRoom', workerId);
            socket.on('sessionQuestionUpdate', handleQuestionUpdate);
        };

        let connectHandler: (() => void) | null = null;
        if (socket.connected) {
            setupListeners();
        } else {
            connectHandler = () => {
                setupListeners();
                socket.off('connect', connectHandler!);
            };
            socket.on('connect', connectHandler);
        }

        return () => {
            socket.emit('leaveSessionRoom', workerId);
            if (connectHandler) socket.off('connect', connectHandler);
            socket.off('sessionQuestionUpdate', handleQuestionUpdate);
        };
    }, [workerId, fetchQuestions, fetchSession]);

    // Persist drafts to localStorage on every change
    useEffect(() => {
        if (Object.keys(editedAnswers).length > 0) {
            saveDraft(draftKey, editedAnswers);
        } else {
            clearDraft(draftKey);
        }
    }, [editedAnswers, draftKey]);

    // Navigation guard
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (dirtyCountRef.current > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    // Handlers
    const handleFieldChange = useCallback((questionId: number, value: string) => {
        setEditedAnswers(prev => ({ ...prev, [questionId]: value }));
        setRestoredDraftCount(0); // dismiss restored banner once user makes changes
    }, []);

    const handleSave = useCallback(async () => {
        if (dirtyQuestionIds.size === 0) return;

        const updates: BulkUpdateAnswerPayloadItem[] = [];
        for (const id of dirtyQuestionIds) {
            const draftValue = editedAnswers[id];
            if (draftValue !== undefined) {
                updates.push({ sessionQuestionId: id, answer: draftValue });
            }
        }

        setSaving(true);
        try {
            await bulkUpdateSessionQuestionAnswers(workerId, updates);
            showSnackbar(`${updates.length} answer${updates.length !== 1 ? 's' : ''} saved successfully!`, 'success');
            setEditedAnswers({});
            clearDraft(draftKey);
            setHasRemoteUpdate(false);
            await fetchQuestions();
        } catch (err: any) {
            showSnackbar(err.message || 'Failed to save answers.', 'error');
        } finally {
            setSaving(false);
        }
    }, [dirtyQuestionIds, editedAnswers, workerId, draftKey, fetchQuestions, showSnackbar]);

    const handleCancel = useCallback(() => {
        setEditedAnswers({});
        clearDraft(draftKey);
        setRestoredDraftCount(0);
    }, [draftKey]);

    const handleReload = useCallback(async () => {
        setEditedAnswers({});
        clearDraft(draftKey);
        setHasRemoteUpdate(false);
        setRestoredDraftCount(0);
        await fetchQuestions();
    }, [draftKey, fetchQuestions]);

    const handleCopyShareLink = useCallback(async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            showSnackbar('Share link copied to clipboard.', 'success');
        } catch {
            showSnackbar('Copy manually: ' + shareUrl, 'info');
        }
    }, [shareUrl, showSnackbar]);

    // List item modal save - adds to draft, doesn't persist immediately
    const handleListItemSave = useCallback((updatedAnswerJson: string) => {
        if (listEditQuestionId === null) return;
        setEditedAnswers(prev => ({ ...prev, [listEditQuestionId]: updatedAnswerJson }));
        setListEditQuestionId(null);
    }, [listEditQuestionId]);

    // Find question for modals
    const historyQuestion = questions.find(q => q.id === historyQuestionId) ?? null;
    const rawListEditQuestion = questions.find(q => q.id === listEditQuestionId) ?? null;
    // ListItemEditModal expects `question.answer` to seed its editable items,
    // but session_questions intentionally no longer carry an `answer` field
    // post-PR1 (answer lives on the worker entity). Resolve via the
    // worker-as-truth helper here so opening the modal pre-populates with
    // the saved items. Without this the modal opens empty even when "N items"
    // is displayed on the row.
    const listEditQuestion = rawListEditQuestion && workerDto
        ? {
              ...rawListEditQuestion,
              answer:
                  editedAnswers[rawListEditQuestion.id]
                  ?? getAnswerForQuestion(rawListEditQuestion as any, workerDto as any),
          }
        : rawListEditQuestion;

    // Loading state
    if (loading && questions.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && questions.length === 0) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            {/* Restored draft banner */}
            {restoredDraftCount > 0 && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={handleCancel}>
                            Discard
                        </Button>
                    }
                >
                    Restored {restoredDraftCount} unsaved change{restoredDraftCount !== 1 ? 's' : ''} from your previous session.
                </Alert>
            )}

            {/* Session info + stats header */}
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.4 : 1),
                }}
            >
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 2,
                }}>
                    {/* Left: status chips */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {session?.aiBotStatus && (
                            <Chip
                                size="small"
                                color={
                                    session.aiBotStatus === 'AWAITING_VERIFICATION' || session.aiBotStatus === 'CV_CONFIRMATION_PENDING'
                                        ? 'warning'
                                        : session.aiBotStatus === 'PROCESSING_MESSAGE' ? 'info' : 'default'
                                }
                                variant="outlined"
                                label={`AI: ${session.aiBotStatus}`}
                            />
                        )}
                        {session?.temporaryAiValidationFeedback && (
                            <Chip size="small" color="info" variant="outlined" label="Awaiting user clarification" />
                        )}
                        {session?.aiBotStatus === 'AWAITING_VERIFICATION' && (
                            <Chip
                                icon={<PendingIcon />}
                                label={
                                    session.verificationAttempts && session.verificationAttempts > 0
                                        ? `Verification (${session.verificationAttempts}/3)`
                                        : 'Verification in progress'
                                }
                                color="info"
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {shareUrl && (
                            <Tooltip title={shareUrl}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<LinkIcon />}
                                    onClick={handleCopyShareLink}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Copy Share Link
                                </Button>
                            </Tooltip>
                        )}
                    </Box>

                    {/* Right: stats */}
                    <Grid container spacing={1} sx={{ maxWidth: { md: 400 } }}>
                        <Grid item xs={4}>
                            <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">Answered</Typography>
                                <Typography variant="h6">{stats.totalAnswered}/{stats.total}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={4}>
                            <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">Required</Typography>
                                <Typography variant="h6">{stats.requiredAnswered}/{stats.requiredTotal}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={4}>
                            <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">Missing</Typography>
                                <Typography variant="h6" color={stats.requiredMissing > 0 ? 'warning.main' : 'text.primary'}>
                                    {stats.requiredMissing}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {/* AI validation feedback */}
            {session?.temporaryAiValidationFeedback && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    AI is awaiting verification/clarification: {session.temporaryAiValidationFeedback}
                </Alert>
            )}

            {/* Remote update + save toolbar */}
            <QuestionFormToolbar
                isDirty={isDirty}
                dirtyCount={dirtyQuestionIds.size}
                saving={saving}
                onSave={handleSave}
                onCancel={handleCancel}
                hasRemoteUpdate={hasRemoteUpdate}
                onReload={handleReload}
            />

            {/* Empty state */}
            {questions.length === 0 && !loading && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <Typography color="text.secondary">
                        No questions found for this worker. Questions will appear here once a session is started.
                    </Typography>
                </Paper>
            )}

            {/* Category sections */}
            <Stack spacing={2}>
                {categoryOrder.map((category) => {
                    const categoryQuestions = groupedQuestions[category];
                    if (!categoryQuestions || categoryQuestions.length === 0) return null;

                    return (
                        <QuestionFormSection
                            key={category}
                            category={category}
                            questions={categoryQuestions}
                            worker={workerDto}
                            editedAnswers={editedAnswers}
                            dirtyQuestionIds={dirtyQuestionIds}
                            onFieldChange={handleFieldChange}
                            onOpenHistory={setHistoryQuestionId}
                            onOpenListEditor={setListEditQuestionId}
                        />
                    );
                })}
            </Stack>

            {/* History Modal */}
            <QuestionHistoryModal
                open={historyQuestionId !== null}
                questionId={historyQuestionId}
                questionLabel={historyQuestion ? getQuestionLabel(historyQuestion) : ''}
                onClose={() => setHistoryQuestionId(null)}
            />

            {/* List Item Edit Modal */}
            {listEditQuestion && (
                <ListItemEditModal
                    open={listEditQuestionId !== null}
                    onClose={() => setListEditQuestionId(null)}
                    question={listEditQuestion}
                    onSave={handleListItemSave}
                />
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default WorkerQuestionForm;
